import { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { MapPin, Loader2, Search, Building2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Search Barbour" modal — available to any user with CRM access (not just
 * super admins). Same postcode + radius project search as the Settings >
 * Integrations test page, just presented as a simple modal from the
 * Sales / CRM dropdown so any rep can use it directly.
 */
/**
 * "Search Barbour" modal — available to any user with CRM access (not just
 * super admins). Same postcode + radius project search as the Settings >
 * Integrations test page, just presented as a simple modal from the
 * Sales / CRM dropdown so any rep can use it directly.
 *
 * Also doubles as "Barbour Search V2" (same UI, different backend route) —
 * V2 calls a standalone Zoho CRM Deluge Function that does the Barbour
 * lookup itself via the Zoho OAuth connection, instead of hitting Barbour's
 * API directly with our own stored Barbour credentials. Pass `apiPath`/
 * `title`/`subtitle`/`testIdPrefix` to point this same component at either.
 */
export const SearchBarbourModal = ({
  isOpen,
  onClose,
  apiPath = '/barbour/search',
  title = 'Search Barbour',
  subtitle = 'Find Barbour ABI projects near a postcode',
  testIdPrefix = 'search-barbour',
}) => {
  const [postcode, setPostcode] = useState('');
  const [radius, setRadius] = useState('5');
  const [maxRadius, setMaxRadius] = useState(5);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const isV2 = apiPath === '/barbour/search-v2';

  // "Barbour Search V2" uses a super-admin-configurable default radius
  // (Settings > Integrations > Barbour > "Barbour Search V2") instead of a
  // hardcoded value — prefill it fresh every time this modal opens in V2 mode.
  // The MAX radius (Settings > Integrations > Barbour) applies everywhere a
  // Barbour search radius is entered, V1 and V2 alike.
  useEffect(() => {
    if (!isOpen) return;
    api.get(`${API}/barbour/max-radius`)
      .then((res) => setMaxRadius(res.data?.max_radius_miles ?? 5))
      .catch(() => {});
    if (isV2) {
      api.get(`${API}/barbour/v2-default-radius`)
        .then((res) => setRadius(String(res.data?.radius_miles ?? '0.1')))
        .catch(() => {});
    }
  }, [isOpen, isV2]);

  if (!isOpen) return null;

  const search = async () => {
    if (!postcode.trim()) return;
    setSearching(true);
    setError('');
    setResults(null);
    try {
      const res = await api.post(`${API}${apiPath}`, {
        postcode: postcode.trim(),
        radius_miles: Math.min(parseFloat(radius) || (isV2 ? 0.1 : 5), maxRadius),
      });
      setResults(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Search failed');
      toast.error(e?.response?.data?.detail || 'Barbour search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') search();
  };

  const handleClose = () => {
    setPostcode('');
    setRadius('5');
    setResults(null);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3" data-testid={`${testIdPrefix}-modal`}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 text-cyan-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-extrabold text-gray-900 tracking-tight leading-tight truncate">{title}</h2>
              <p className="text-[11px] text-gray-500 truncate">{subtitle}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0" data-testid={`${testIdPrefix}-modal-close`}>
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1 block">Postcode</label>
              <Input
                placeholder="e.g. SW1A 1AA"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                data-testid={`${testIdPrefix}-postcode-input`}
              />
            </div>
            <div className="w-28">
              <label className="text-xs text-gray-500 mb-1 block">Radius (miles)</label>
              <Input
                type="number"
                min="0.05"
                max={maxRadius}
                step="0.05"
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                onKeyDown={handleKeyDown}
                data-testid={`${testIdPrefix}-radius-input`}
              />
            </div>
            <Button onClick={search} disabled={searching || !postcode.trim()} data-testid={`${testIdPrefix}-search-btn`}>
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-3.5 h-3.5 mr-1" /> Search</>}
            </Button>
          </div>

          {error && (
            <p className="text-sm text-red-600" data-testid={`${testIdPrefix}-error`}>{error}</p>
          )}

          {results && (
            <div data-testid={`${testIdPrefix}-results`}>
              <p className="text-xs text-gray-500 mb-2">
                Showing {results.count} of {results.total_matching} project{results.total_matching === 1 ? '' : 's'} within {results.radius_miles} mile{results.radius_miles === 1 ? '' : 's'} of {results.postcode}
              </p>
              {results.projects.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">No projects found in that radius.</p>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">Barbour ID</th>
                        <th className="text-left px-3 py-2">Project Name</th>
                        <th className="text-left px-3 py-2">Address / Postcode</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {results.projects.map((p) => (
                        <tr key={p.barbour_id} data-testid={`${testIdPrefix}-result-row-${p.barbour_id}`}>
                          <td className="px-3 py-2 font-mono text-gray-700">{p.barbour_id}</td>
                          <td className="px-3 py-2 text-gray-900">{p.project_name}</td>
                          <td className="px-3 py-2 text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3 shrink-0" />
                            {[p.address, p.postcode].filter(Boolean).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
